import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import axios from "axios";
import { 
  Upload, 
  FileText, 
  CheckCircle2, 
  XCircle, 
  Clock, 
  Trash2,
  AlertCircle,
  File,
  Image,
  FileSpreadsheet
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DOC_TYPE_LABELS = {
  id_proof: { label: "Photo ID", icon: FileText, desc: "Passport, driving licence, or national ID" },
  address_proof: { label: "Proof of Address", icon: FileText, desc: "Utility bill, bank statement (last 3 months)" },
  bank_statements: { label: "Bank Statements", icon: FileSpreadsheet, desc: "Last 3 months for all accounts" },
  payslips: { label: "Payslips", icon: FileText, desc: "Last 3 months of payslips" },
  creditor_statements: { label: "Creditor Statements", icon: FileText, desc: "Recent statements from creditors" },
  employment_contract: { label: "Employment Contract", icon: FileText, desc: "Current employment contract" },
  benefit_letters: { label: "Benefit Letters", icon: FileText, desc: "DWP or benefit award letters" },
  other: { label: "Other Documents", icon: File, desc: "Any other supporting documents" }
};

const STATUS_STYLES = {
  pending: { label: "Pending Review", icon: Clock, color: "text-amber-500 bg-amber-50" },
  approved: { label: "Approved", icon: CheckCircle2, color: "text-green-600 bg-green-50" },
  rejected: { label: "Rejected", icon: XCircle, color: "text-red-500 bg-red-50" }
};

const DocumentsPage = () => {
  const { token } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(null);
  const fileInputRefs = useRef({});

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const [docsRes, reqsRes] = await Promise.all([
        axios.get(`${API}/documents`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/documents/requests`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setDocuments(docsRes.data);
      setRequests(reqsRes.data);
    } catch (error) {
      console.error("Documents error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (docType, file) => {
    if (!file) return;
    
    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large. Maximum size is 10MB.");
      return;
    }
    
    setUploading(docType);
    
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("document_type", docType);
      
      await axios.post(`${API}/documents/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      
      toast.success("Document uploaded successfully!");
      fetchData();
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(null);
    }
  };

  const handleDelete = async (docId) => {
    if (!confirm("Are you sure you want to delete this document?")) return;
    
    try {
      await axios.delete(`${API}/documents/${docId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Document deleted");
      fetchData();
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete document");
    }
  };

  const getDocumentsForType = (type) => {
    return documents.filter(d => d.document_type === type);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-[#10B981] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  const requiredDocs = requests.filter(r => r.required && !r.fulfilled);
  const completedDocs = requests.filter(r => r.fulfilled);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="documents-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-[#0F172A] mb-2">Documents</h1>
          <p className="text-slate-600">Upload required documents to progress your IVA application</p>
        </div>

        {/* Required Documents Alert */}
        {requiredDocs.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-amber-50 border border-amber-200 rounded-2xl p-6"
          >
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="font-semibold text-amber-800 mb-1">Documents Required</h3>
                <p className="text-amber-700 text-sm">
                  Please upload the following {requiredDocs.length} document(s) to continue with your application.
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Document Categories */}
        <div className="grid gap-4">
          {requests.map((request, index) => {
            const typeInfo = DOC_TYPE_LABELS[request.document_type] || DOC_TYPE_LABELS.other;
            const TypeIcon = typeInfo.icon;
            const uploadedDocs = getDocumentsForType(request.document_type);
            const isUploading = uploading === request.document_type;
            
            return (
              <motion.div
                key={request.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`bg-white rounded-2xl border ${
                  request.fulfilled ? 'border-green-200' : 'border-slate-200'
                } overflow-hidden`}
                data-testid={`doc-section-${request.document_type}`}
              >
                <div className="p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        request.fulfilled ? 'bg-green-100' : 'bg-slate-100'
                      }`}>
                        <TypeIcon className={`w-6 h-6 ${
                          request.fulfilled ? 'text-green-600' : 'text-slate-500'
                        }`} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-[#0F172A]">{typeInfo.label}</h3>
                          {request.required && !request.fulfilled && (
                            <span className="px-2 py-0.5 bg-red-100 text-red-600 text-xs font-medium rounded-full">
                              Required
                            </span>
                          )}
                          {request.fulfilled && (
                            <span className="px-2 py-0.5 bg-green-100 text-green-600 text-xs font-medium rounded-full">
                              Uploaded
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-500 mt-1">{request.description}</p>
                      </div>
                    </div>
                    
                    <div>
                      <input
                        type="file"
                        ref={(el) => fileInputRefs.current[request.document_type] = el}
                        onChange={(e) => handleUpload(request.document_type, e.target.files[0])}
                        className="hidden"
                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                      />
                      <Button
                        onClick={() => fileInputRefs.current[request.document_type]?.click()}
                        disabled={isUploading}
                        variant={request.fulfilled ? "outline" : "default"}
                        className={request.fulfilled ? "" : "bg-[#10B981] hover:bg-[#059669]"}
                        data-testid={`upload-btn-${request.document_type}`}
                      >
                        {isUploading ? (
                          <span className="flex items-center gap-2">
                            <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                            Uploading...
                          </span>
                        ) : (
                          <>
                            <Upload className="w-4 h-4 mr-2" />
                            {request.fulfilled ? "Upload Another" : "Upload"}
                          </>
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Uploaded Documents */}
                  {uploadedDocs.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-slate-100 space-y-3">
                      {uploadedDocs.map((doc) => {
                        const statusStyle = STATUS_STYLES[doc.status];
                        const StatusIcon = statusStyle.icon;
                        
                        return (
                          <div
                            key={doc.id}
                            className="flex items-center justify-between p-3 bg-slate-50 rounded-xl"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center border border-slate-200">
                                {doc.mime_type?.includes('image') ? (
                                  <Image className="w-5 h-5 text-slate-400" />
                                ) : (
                                  <FileText className="w-5 h-5 text-slate-400" />
                                )}
                              </div>
                              <div>
                                <p className="font-medium text-[#0F172A] text-sm truncate max-w-[200px]">
                                  {doc.file_name}
                                </p>
                                <p className="text-xs text-slate-500">
                                  {formatFileSize(doc.file_size)} • {new Date(doc.uploaded_at).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium ${statusStyle.color}`}>
                                <StatusIcon className="w-3.5 h-3.5" />
                                {statusStyle.label}
                              </span>
                              <button
                                onClick={() => handleDelete(doc.id)}
                                className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Upload Guidelines */}
        <div className="bg-slate-50 rounded-2xl p-6">
          <h3 className="font-semibold text-[#0F172A] mb-4">Upload Guidelines</h3>
          <ul className="space-y-2 text-sm text-slate-600">
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-[#10B981] mt-0.5 flex-shrink-0" />
              Accepted formats: PDF, JPG, PNG, DOC, DOCX
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-[#10B981] mt-0.5 flex-shrink-0" />
              Maximum file size: 10MB per document
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-[#10B981] mt-0.5 flex-shrink-0" />
              Ensure documents are clear and readable
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-[#10B981] mt-0.5 flex-shrink-0" />
              All four corners of documents should be visible
            </li>
          </ul>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default DocumentsPage;
